<script lang="ts">
    import { faRecycle, faPaperPlane, faRefresh, faBan, faFileInvoiceDollar, faCheck, faRotateLeft, faQrcode } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher, onMount, tick } from 'svelte'
    import type { RefundChargePreviewDto } from '../../modules/paymentsApi/charges/dto/refund-charge.dto'
    const dispatch = createEventDispatcher()
    import type { Charge } from '../../modules/paymentsApi/charges/charge.entity'
    import { ChargeStatus, ChargeStatusFriendly, PaymentMethodsFriendly } from '../../modules/paymentsApi/charges/charge.enum'
    import Fa from 'svelte-fa'
    import { faCode, faCopy } from '@fortawesome/free-solid-svg-icons'
    import Popover from '../_common/popover.svelte'
    import { PurchaseStatus } from '../../modules/paymentsApi/purchases/purchase.enum'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zact from '../_common/z/zact.svelte'
    import { DetailsClass, GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine, ZDetailItem } from '../_common/z/zgrid'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zdetails from '../_common/z/zdetails.svelte'
    import { Container } from 'sveltestrap'
    import type { PurchaseInvoice } from '../../modules/paymentsApi/charges/invoice.entity'
    import { SendMessageTemplate } from '../_common/send-message.template'
    import Zinput from '../_common/z/zinput.svelte'
    import PaymentsPixShow from './payments-pix-show.svelte'
    import type { Gateway } from '../../modules/paymentsApi/gateways/gateway.entity'
    import Zdrop from '../_common/z/zdrop.svelte'
    import type { ZdropInterface } from '../_common/z/zdrop.interface'

    export let personId: string
    export let purchaseStatus: PurchaseStatus = null
    export let charge: Charge = null

    let show = false

    async function goCancelPurchase() {
        if (!confirm('Deseja mesmo CANCELAR esta parcela?\nLembre-se: esta ação é irreversível.')) {
            return
        }
        await window.api.payments.charges.void(charge.chargeId)
        dispatch('close')
    }

    // Estorno é operação de PARCELA, e por isso mora aqui: cancelar uma assinatura é ato local e
    // não devolve dinheiro de nada. O que o gateway faz depende do estado — parcela não paga é
    // invalidada, parcela paga é devolvida — e quem sabe disso é o backend, não esta tela.
    let refund: RefundChargePreviewDto = null
    let estornando = false

    async function carregarRefund() {
        try {
            refund = await window.api.payments.charges.refundPreview(charge.chargeId)
        } catch (error) {
            // sem o preview a tela ainda funciona: o botão só não aparece
            refund = null
        }
        // o histórico ganha o botão de estorno por pagamento, e quem diz o saldo de cada um é o
        // preview — por isso o grid é remontado quando ele chega
        if (charge) loadGrid()
    }

    /**
     * Estorno de UM pagamento da parcela.
     *
     * A parcela pode ter mais de um no gateway: um boleto avulso que o cliente pagou e a cobrança
     * que saiu no cartão enquanto o banco compensava. Cada um é uma transação lá, com saldo próprio,
     * e desfazer um não mexe no outro — por isso a escolha acontece na linha do histórico, e não no
     * menu da parcela.
     */
    async function goRefundInvoice(invoiceId: string) {
        try {
            show = true
            const previa = await window.api.payments.charges.refundPreview(charge.chargeId, undefined, invoiceId)
            if (!previa?.allowed) {
                window.error(previa?.reason || 'Não é possível estornar este pagamento.')
                return
            }
            const acao = previa.outcome === 'refunded' ? 'ESTORNAR' : 'CANCELAR no gateway'
            const valor = window.api.utils.utils.toReal(previa.refundable)
            const meio = PaymentMethodsFriendly[previa.paymentMethod] || previa.paymentMethod
            if (!confirm(`Deseja mesmo ${acao} ${valor} do pagamento por ${meio}?\nLembre-se: esta ação é irreversível.`)) {
                return
            }
            estornando = true
            await window.api.payments.charges.refund(charge.chargeId, { invoiceId })
            dispatch('close')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            estornando = false
            show = false
        }
    }

    // o pagamento correspondente a esta linha do histórico, com o saldo que ainda dá para devolver
    function pagamentoDoInvoice(invoiceId: string) {
        return (refund?.payments || []).find((el) => el.invoiceId === invoiceId)
    }

    /**
     * A régua de escrita da tela, usada pelo menu E pelo histórico.
     *
     * Existe como função única de propósito: o estorno por pagamento nasceu conferindo apenas a
     * permissão, e por isso continuava aparecendo na linha de uma assinatura cancelada — onde o
     * menu já não oferecia nada. Duas cópias da mesma regra divergem.
     */
    function podeEditar(): boolean {
        return window.store.token.permissions.pay.entries.write && purchaseStatus !== PurchaseStatus.canceled
    }

    async function goRefund() {
        if (!refund?.allowed) return
        const acao = refund.outcome === 'refunded' ? 'ESTORNAR' : 'CANCELAR no gateway'
        const valor = window.api.utils.utils.toReal(refund.refundable)
        if (!confirm(`Deseja mesmo ${acao} ${valor} desta parcela?\nLembre-se: esta ação é irreversível.`)) {
            return
        }
        try {
            estornando = true
            await window.api.payments.charges.refund(charge.chargeId, {})
            dispatch('close')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            estornando = false
        }
    }

    async function goPaidPurchase() {
        if (!confirm('Deseja mesmo registrar esta parcela como paga?\nLembre-se: esta ação é irreversível.')) {
            return
        }
        await window.api.payments.charges.paid(charge.chargeId)
        dispatch('close')
    }

    let pixChargeId = null
    function showPix(chargeId: string) {
        pixChargeId = chargeId
    }

    let bankslipUrl = null
    async function goBoleto() {
        if (!confirm('Deseja mesmo gerar um boleto avulso para esta parcela?\nAtenção: caso já haja um boleto a vencer o novo boleto não será gerado.')) {
            return
        }
        try {
            show = true
            const result = await window.api.payments.charges.singleCharge(charge.chargeId, 'bankslip')
            // A api responde 200 mesmo quando o boleto não sai: cobrança recusada é desfecho de
            // negócio registrado, não erro de transporte. Sem ler este status a tela seguia para o
            // boleto e o motivo real ficava só no log.
            if (result?.status === 'error' || !result?.invoiceId) {
                window.error(result?.message || 'Não foi possível emitir o boleto para esta parcela.')
                return
            }
            await showBoleto(charge.chargeId, result.invoiceId)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function goPix() {
        if (!confirm('Deseja mesmo gerar um pix avulso para esta parcela?\nAtenção: caso já haja um pix válido a vencer, ele será reaproveitado.')) {
            return
        }
        try {
            show = true
            const result = await window.api.payments.charges.singleCharge(charge.chargeId, 'pix')
            // mesma leitura do boleto: a api responde 200 mesmo quando não emite
            if (result?.status === 'error') {
                window.error(result?.message || 'Não foi possível emitir o pix para esta parcela.')
                return
            }
            showPix(charge.chargeId)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    // O meio só aparece se o gateway DESTA parcela aceitar. A parcela ainda não cobrada não tem
    // gateway fixado, e aí vale o da assinatura. As capacidades vêm calculadas da api, a partir do
    // adapter de cada gateway — não são uma lista mantida aqui.
    function gatewayAceita(metodo: string, c: Charge, lista: Gateway[]): boolean {
        const gatewayId = c?.gatewayId || c?.purchase?.gatewayId
        if (!gatewayId) return true
        const gateway = lista?.find((el) => el.gatewayId === gatewayId)
        if (!gateway) return true
        const meios = gateway.gatewayPaymentMethods || []
        return !meios.length || meios.includes(metodo)
    }

    function allowRetry() {
        // se ainda não está vencido...
        if (charge.dueDate && new Date(charge.dueDate).getTime() > new Date().getTime()) {
            return false
        }
        if (charge.status === ChargeStatus.declined) return true
        if (charge.status === ChargeStatus.failed) return true
        if (charge.status === ChargeStatus.pending) return true
        if (charge.status === ChargeStatus.processing) return true
        if (charge.status === ChargeStatus.scheduled) return true
        // espelha o allowRetry do send-charge: documento vencido pode ser cobrado de novo
        if (charge.status === ChargeStatus.expired) return true
        return false
    }

    async function goRetry() {
        if (!confirm('Deseja mesmo tentar cobrar novamente esta parcela?')) {
            return
        }
        try {
            show = true
            const result = await window.api.payments.charges.retry(charge.chargeId)
            dispatch('close')
            // 'gatewayError' distingue "não foi possível enviar" de "enviada e pendente". A
            // resposta é 200 nos dois casos, então anunciar sucesso sem olhar isso escondia a falha.
            if (result?.gatewayError) {
                window.error(result?.message || 'A cobrança não pôde ser enviada ao gateway.')
                return
            }
            window.alert('Uma nova tentativa de cobrança foi solicitada.')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function goSync() {
        await window.api.payments.charges.gatewaySync(charge.chargeId)
        dispatch('close')
        window.alert('A cobrança foi sincronizada com seu gateway de pagamentos.')
    }

    let showResponse = null
    let showResponseDiv
    async function goShowResponse(invoiceId) {
        const found = charge.invoices.find((el) => el.invoiceId === invoiceId)
        console.log('show-resp', invoiceId)
        showResponse = invoiceId
        await tick()
        console.log(beautifyJson(found.gatewayResponse))
        showResponseDiv.innerHTML = beautifyJson(found.gatewayResponse)
    }

    function beautifyJson(json) {
        if (!json) return ''
        return JSON.stringify(JSON.parse(json), null, 4)
    }

    async function showBoleto(chargeId: string, invoiceId: string) {
        const boleto = await window.api.payments.charges.getBoleto(chargeId)
        if (!boleto?.bankslipUrl) {
            window.error('Ocorreu um erro ao tentar imprimir o boleto.')
            return
        }
        bankslipUrl = boleto.bankslipUrl
    }
    function closeBoleto() {
        bankslipUrl = null
        dispatch('close')
    }

    let linkField
    function copyLink() {
        linkField.select()
        linkField.setSelectionRange(0, 99999) /* For mobile devices */
        window.api.utils.utils.copyToClipboard(linkField.value)
        window.alert('Link copiado')
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Histórico de Tentativas'
        grid.showActs = false
        grid.subtitle = ''
        grid.cols.push(new GridCol({ content: 'Emissão', type: GridColTypeEnum.date }))
        // o meio de cada tentativa é o que dá sentido à data ao lado: a mesma parcela pode ter um
        // boleto e um pix, e cada um vale até uma data diferente
        grid.cols.push(new GridCol({ content: 'Meio' }))
        // Validade do DOCUMENTO, não prazo da parcela. No boleto é o vencimento impresso; no pix é o
        // instante em que o QR Code morre, que vale o ciclo inteiro e chega a ficar meses à frente.
        // Chamar isso de "vencimento" fazia parecer que a mensalidade tinha todo esse prazo — o
        // vencimento dela é o de "Dados da Parcela", acima.
        grid.cols.push(new GridCol({ content: 'Válido até', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'ID' }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        let index = 0
        for (const item of charge.invoices) {
            let btns = []
            if (item.paymentMethod === 'bankslip') {
                const btnBoleto = new GridButton({
                    callback: () => showBoleto(item.chargeId, item.invoiceId),
                    label: 'Boleto',
                })
                btns.push(btnBoleto)
            }
            if (item.paymentMethod === 'pix') {
                const btnPix = new GridButton({
                    callback: () => showPix(item.chargeId),
                    label: 'Pix',
                })
                btns.push(btnPix)
            }
            // Estorno na LINHA: o dinheiro é do pagamento, não da parcela. Aparece só onde há saldo
            // a devolver, e o rótulo diz o que vai acontecer — pagamento concluído devolve valor,
            // documento em aberto apenas deixa de ser pagável.
            const pagamento = pagamentoDoInvoice(item.invoiceId)
            if (pagamento?.refundable > 0 && podeEditar()) {
                btns.push(
                    new GridButton({
                        callback: () => goRefundInvoice(item.invoiceId),
                        icon: faRotateLeft,
                        label: pagamento.paid ? 'Estornar' : 'Cancelar',
                        // vermelho como era no botão da barra: devolver dinheiro é irreversível
                        color: 'red',
                    }),
                )
            }

            const btnCode = new GridButton({
                callback: () => goShowResponse(item.invoiceId),
                icon: faCode,
            })
            btns.push(btnCode)
            index++
            let line: GridLine = new GridLine()
            line.slots.push({ content: item?.createdAt })
            line.slots.push({ content: PaymentMethodsFriendly[item?.paymentMethod] || item?.paymentMethod || '-' })
            line.slots.push({ content: item?.dueDate })
            line.slots.push({ content: new GridBadge({ label: ChargeStatusFriendly[item.statusLabel], class: getBadgeColor(item.status) }) })
            line.slots.push({ content: item?.invoiceId })
            line.slots.push({ content: btns })
            grid.lines.push(line)
        }
    }

    function getBadgeColor(status: ChargeStatus) {
        if (status === ChargeStatus.scheduled) return GridBadgeClassEnum.badge_default
        if (status === ChargeStatus.processing) return GridBadgeClassEnum.badge_alert
        if (status === ChargeStatus.canceled) return GridBadgeClassEnum.badge_alert
        if (status === ChargeStatus.paid) return GridBadgeClassEnum.badge_success
        if (status === ChargeStatus.paid_manual) return GridBadgeClassEnum.badge_success
        if (status === ChargeStatus.pending) return GridBadgeClassEnum.badge_alert
        // documento vencido: a dívida continua em aberto, então é aviso, não encerramento
        if (status === ChargeStatus.expired) return GridBadgeClassEnum.badge_alert
        if (status === ChargeStatus.declined) return GridBadgeClassEnum.badge_danger
        if (parseInt(status.toString()) === 100) return GridBadgeClassEnum.badge_success
        if (parseInt(status.toString()) === 80) return GridBadgeClassEnum.badge_danger
        if (parseInt(status.toString()) === 5) return GridBadgeClassEnum.badge_danger
        if (parseInt(status.toString()) === 20) return GridBadgeClassEnum.badge_alert

        return null
    }

    // O gateway é gravado por id na parcela; a lista serve só para exibir o nome no lugar do uuid.
    //
    // A lista entra como parâmetro de propósito: sem ela na expressão do template o Svelte não sabe
    // que o texto depende de `gateways`, e a tela — que renderiza antes do fetch terminar — ficaria
    // congelada mostrando o uuid.
    let gateways: Gateway[] = []
    function gatewayLabel(gatewayId: string, lista: Gateway[]): string {
        if (!gatewayId) return null
        return lista?.find((el) => el.gatewayId === gatewayId)?.label || gatewayId
    }

    /**
     * As ações da parcela, em menu — o mesmo formato da tela de Cobrança.
     *
     * Eram até sete botões disputando a mesma linha, e no celular a barra quebrava em fileiras. O
     * menu mantém os ícones e as explicações de cada ação; o que muda é que elas deixam de ocupar
     * a tela toda o tempo inteiro.
     *
     * Montado por função reativa, e não na declaração: quase toda condição depende de dados que
     * chegam depois (a parcela, o preview de estorno e a lista de gateways).
     */
    $: menu = montaMenu(charge, refund, gateways, estornando)

    function montaMenu(c: Charge, r: RefundChargePreviewDto, lista: Gateway[], emEstorno: boolean): ZdropInterface {
        const buttons: ZdropInterface['buttons'] = [
            {
                callback: goSync,
                label: 'Sincronizar',
                icon: faRefresh,
                help: 'Você pode sincronizar a cobrança com seu gateway de pagamentos manualmente. Utilize quando fizer alterações direto no painel do gateway (ex.: aprovar uma cobrança manualmente).',
            },
        ]
        const menuFormato: ZdropInterface = { format: 'square', position: 'flow', positionMargin: '4px', buttons }

        if (!c || !podeEditar()) return menuFormato

        const emAberto =
            c.status === ChargeStatus.scheduled ||
            c.status === ChargeStatus.declined ||
            c.status === ChargeStatus.pending ||
            c.status === ChargeStatus.processing ||
            // documento vencido é parcela em aberto: é justamente quando se precisa emitir outro,
            // receber ou cancelar. Sem isto o menu ficava sem nenhuma dessas ações.
            c.status === ChargeStatus.expired

        if (emAberto) buttons.push({ callback: goCancelPurchase, label: 'Cancelar', icon: faBan })

        buttons.push({
            callback: goRetry,
            label: 'Retentativa',
            icon: faRecycle,
            disabled: !allowRetry(),
            help: 'Você pode tentar cobrar manualmente (essa opção nem sempre estará disponível).',
        })

        if (r?.allowed) {
            // o rótulo diz o que vai acontecer de fato: parcela paga devolve dinheiro, parcela em
            // aberto só deixa de ser pagável no gateway
            buttons.push({
                callback: goRefund,
                label: r.outcome === 'refunded' ? 'Estornar' : 'Cancelar no gateway',
                icon: faRotateLeft,
                disabled: emEstorno,
                help:
                    r.outcome === 'refunded'
                        ? 'Devolve o valor ao cliente pelo gateway (' + r.gatewayLabel + ').'
                        : 'Invalida a cobrança no gateway (' + r.gatewayLabel + '), para que não possa mais ser paga.',
            })
        }

        if (emAberto || c.status === ChargeStatus.canceled) {
            buttons.push({ callback: goPaidPurchase, label: 'Receber', icon: faCheck })
        }

        if (emAberto || c.status === ChargeStatus.failed) {
            // o meio só entra se o gateway DESTA parcela aceitar (ver gatewayAceita)
            if (gatewayAceita('bankslip', c, lista)) {
                buttons.push({ callback: goBoleto, label: 'Boleto avulso', icon: faFileInvoiceDollar })
            }
            if (gatewayAceita('pix', c, lista)) {
                buttons.push({ callback: goPix, label: 'Pix avulso', icon: faQrcode })
            }
        }

        return menuFormato
    }

    $: details = new DetailsClass({ items: charge ? buildDetailItems(charge, gateways) : [] })

    function buildDetailItems(c: Charge, gatewaysList: Gateway[]): ZDetailItem[] {
        return [
            // gateway DESTA parcela: fixado no momento em que ela foi cobrada, e não necessariamente
            // o gateway atual da assinatura. é onde o pagamento dela acontece e onde ele é conferido.
            new ZDetailItem({ label: 'Gateway', content: gatewayLabel(c.gatewayId, gatewaysList) || 'ainda não enviada a um gateway', size: 4 }),
            new ZDetailItem({ label: 'ID da parcela', content: c.chargeId, size: 4 }),
            new ZDetailItem({ label: 'Vencimento', type: GridColTypeEnum.date, content: c.dueDate, size: 4 }),
            new ZDetailItem({
                label: 'Status',
                type: GridColTypeEnum.badge,
                content: new GridBadge({ label: ChargeStatusFriendly[c.statusLabel], class: getBadgeColor(c.status) }),
                size: 4,
            }),
            new ZDetailItem({ label: 'Pago em', type: GridColTypeEnum.date, content: c.paidAt, size: 4 }),
            new ZDetailItem({ label: 'Tentativas de cobrança', type: GridColTypeEnum.int, content: c.tryTimes, size: 4 }),
            new ZDetailItem({ label: 'Última tentativa', type: GridColTypeEnum.date, content: c.tryLast, size: 4 }),
            new ZDetailItem({ label: 'Próxima tentativa', type: GridColTypeEnum.date, content: c.tryNext, size: 4 }),
        ]
    }

    onMount(async () => {
        try {
            gateways = await window.api.payments.gateways.getAll()
        } catch (error) {
            // sem a lista a tela ainda funciona: cai no id
            gateways = []
        }
        await carregarRefund()
        loadGrid()
    })

    let acts = []
    acts.push({
        callback: sendMessage,
        label: 'Enviar',
        icon: faPaperPlane,
        color: 'green',
    })

    function sendMessage() {
        // não funcional, pois o user não possui um personId
        window.message(SendMessageTemplate.share_link_02(linkField, 'Boleto', personId))
    }
</script>

<LoadSpinner {show}></LoadSpinner>
{#if showResponse}
    <Popover on:close={() => (showResponse = null)} width="970px" height="200px">
        <div bind:this={showResponseDiv} class="config-box m-4 show-response" />
    </Popover>
{/if}

{#if bankslipUrl}
    <Popover on:close={closeBoleto} width="670px" height="50px">
        <Zframe title="Boleto avulso" {acts} class="m-3">
            <Zinput showCopy={true} disabled={true} label="Boleto" value={bankslipUrl}></Zinput>
        </Zframe>
    </Popover>
{/if}

{#if pixChargeId}
    <Popover title="Pix" width="560px" height="420px" on:close={() => (pixChargeId = null)}>
        <!-- tela interna: só exibe o QR já gerado, sem ficar consultando pagamento -->
        <PaymentsPixShow chargeId={pixChargeId} watchPayment={false} on:close={() => (pixChargeId = null)} />
    </Popover>
{/if}

{#if charge}
    <Container>
        <div class="purchase-show">
            <!-- ações em menu (ver montaMenu), como na tela de Cobrança -->
            <Zact class="h-space">
                <div class="spacer" />
                <Zdrop {menu}></Zdrop>
            </Zact>
            <Zframe title="Dados da Parcela" class="h-space">
                <Zdetails {details} />
            </Zframe>
            <Zgrid class="h-space mb-2" {grid}></Zgrid>
        </div>
    </Container>
{/if}

<style>
    .show-response {
        white-space: pre-wrap;
        font-family: monospace;
        font-size: 13px;
    }
    .grid {
        font-size: 14px;
    }

    .token-link {
        width: 100%;
        word-wrap: break-word;
        border: 1px solid #99999930;
        background-color: #99999922;
        border-radius: 4px;
        padding: 6px;
        text-align: center;
        font-size: 14px;
    }
</style>
